/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import Modal from "../secondary/Modal";
import { db } from "../../firebase";
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faPenToSquare, faTrashAlt, faX } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import { mostrarError, mostrarExito, mostrarInfo } from "../../utils/Alertas";
import { TextField, InputLabel, Select, MenuItem, FormControl } from "@mui/material";
import "../ideas/css/Ideas.css";
import "./css/Ranking.css";

interface Item {
    id: string;
    name: string;
}

interface Category {
    id: string;
    name: string;
    items: Item[];
    isEditing: boolean;
}

const Ranking: React.FC = () => {
    const dispatch = useDispatch();
    const [categories, setCategories] = useState<Category[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [newItemName, setNewItemName] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingCategories, setEditingCategories] = useState<Set<string>>(new Set());
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            dispatch(showLoading());
            try {
                const categoriesSnapshot = await getDocs(collection(db, "categories"));
                const categoriesData = categoriesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as Category[];
                setCategories(categoriesData);
                setSelectedCategory(categoriesData[0].id);
            } catch (error) {
                console.error("Error buscando categorias: ", error);
                mostrarError('Error buscando categorias.');
            } finally {
                dispatch(hideLoading());
            }
        };

        fetchData();
    }, []);

    const handleSaveCategory = async () => {
        if (newCategoryName.trim() === "") {
            mostrarInfo('Por favor, ingresa una nueva categoria.');
            return;
        }

        const newCategory = {
            name: newCategoryName,
            items: [],
            isEditing: false
        };

        dispatch(showLoading());
        try {
            const docRef = await addDoc(collection(db, "categories"), newCategory);
            setCategories([...categories, { ...newCategory, id: docRef.id }]);
            setSelectedCategory(docRef.id);
            setNewCategoryName("");
            setIsModalOpen(false);
            mostrarExito('Categoría añadida correctamente.');
        } catch (error) {
            console.error("Error añadiendo categoria: ", error);
            mostrarError('Error añadiendo categoría.');
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleAddItem = async () => {
        if (newItemName.trim() === "") {
            mostrarInfo('Por favor, ingresa un producto.');
            return;
        } else if (selectedCategory === "") {
            mostrarInfo('Por favor, seleccione una categoria.');
            return;
        }

        const categoryIndex = categories.findIndex(
            (cat) => cat.id === selectedCategory
        );
        if (categoryIndex === -1) return;

        const newItem: Item = {
            id: Date.now().toString(),
            name: newItemName,
        };

        const updatedCategories = [...categories];
        updatedCategories[categoryIndex].items.push(newItem);

        dispatch(showLoading());
        try {
            const categoryRef = doc(db, "categories", selectedCategory);
            await updateDoc(categoryRef, {
                items: updatedCategories[categoryIndex].items,
            });
            setCategories(updatedCategories);
            setNewItemName("");
            mostrarExito('Producto añadido correctamente.');
        } catch (error) {
            console.error("Error añadiendo producto: ", error);
            mostrarError('Error añadiendo producto.');
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleDragEnd = async (result: DropResult, categoryId: string) => {
        if (!result.destination) return;

        const { source, destination } = result;

        if (!destination) return;

        const newCategories = categories.map((category) => {
            if (category.id === categoryId) {
                const newItems = Array.from(category.items);
                const [movedItem] = newItems.splice(source.index, 1);
                newItems.splice(destination.index, 0, movedItem);

                // Actualiza Firestore
                const categoryRef = doc(db, "categories", categoryId);
                updateDoc(categoryRef, { items: newItems });

                return { ...category, items: newItems };
            }
            return category;
        });

        setCategories(newCategories);
    };

    const handleEditCategory = (categoryId: string) => {
        setEditingCategories((prevEditingCategories) => {
            const newEditingCategories = new Set(prevEditingCategories);
            if (newEditingCategories.has(categoryId)) {
                newEditingCategories.delete(categoryId);
            } else {
                newEditingCategories.add(categoryId);
            }
            return newEditingCategories;
        });
    };

    const handleDeleteItem = async (categoryId: string, itemId: string) => {
        dispatch(showLoading());
        try {
            // Encuentra la categoría que contiene el ítem
            const categoryIndex = categories.findIndex((cat) => cat.id === categoryId);
            if (categoryIndex === -1) return;

            // Filtra el ítem a eliminar
            const updatedItems = categories[categoryIndex].items.filter((item) => item.id !== itemId);

            // Actualiza Firestore
            const categoryRef = doc(db, "categories", categoryId);
            await updateDoc(categoryRef, { items: updatedItems });

            // Actualiza el estado local
            const updatedCategories = [...categories];
            updatedCategories[categoryIndex].items = updatedItems;
            setCategories(updatedCategories);
            mostrarExito('Producto eliminado correctamente.');
        } catch (error) {
            console.error("Error eliminando item: ", error);
            mostrarError('Error eliminando item.');
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleDeleteCategory = async (categoryId: string) => {
        setCategoryToDelete(categoryId);
        setIsConfirmModalOpen(true);
    };

    const confirmDeleteCategory = async () => {
        if (!categoryToDelete) return;

        dispatch(showLoading());
        try {
            // Referencia del documento de la categoría a eliminar
            const categoryRef = doc(db, "categories", categoryToDelete);
            await deleteDoc(categoryRef);

            // Actualiza el estado local
            const updatedCategories = categories.filter(category => category.id !== categoryToDelete);
            setCategories(updatedCategories);
            setCategoryToDelete(null);
            setIsConfirmModalOpen(false);
            mostrarExito('Categoría eliminada correctamente.');
        } catch (error) {
            console.error("Error eliminando categoría: ", error);
            mostrarError('Error eliminando categoría.');
        } finally {
            dispatch(hideLoading());
        }
    };

    // Función para cancelar la eliminación
    const cancelDeleteCategory = () => {
        setCategoryToDelete(null);
        setIsConfirmModalOpen(false);
    };

    return (
        <div className="container-fluid ranking-page">
            <div className="text-center">
                <h2>Ranking</h2>
            </div>

            <div className="container-fluid content pb-3">
                <div
                    className="toggle-form"
                    onClick={() => setIsFormOpen(!isFormOpen)}
                >
                    {/* <span>{isFormOpen ? 'Ocultar Formulario' : 'Mostrar Formulario'}</span> */}
                    <FontAwesomeIcon
                        icon={isFormOpen ? faChevronUp : faChevronDown}
                        className="icon-toggle"
                    />
                </div>

                {isFormOpen && (
                    <form onSubmit={(e) => { e.preventDefault(); handleAddItem(); }}>
                        <div className="input-group">
                            <TextField
                                label="Ingresa el producto"
                                id="item"
                                value={newItemName}
                                onChange={(e) => setNewItemName(e.target.value)}
                                fullWidth
                                margin="normal"
                                style={{ marginTop: 0 }}
                            />
                        </div>
                        <FormControl fullWidth margin="normal" disabled={categories.length === 0} style={{ marginTop: 0 }}>
                            <InputLabel id="category-label">Selecciona la categoría</InputLabel>
                                <Select
                                    labelId="category-label"
                                    id="category"
                                    value={selectedCategory}
                                    label="Selecciona la categoría"
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                        </FormControl>
                        <button className="main-button">Añadir</button>
                        <div className="text-center my-3">
                            <a className="main-color" style={{ textDecoration: "none", fontWeight: "bold" }} onClick={() => setIsModalOpen(true)}>Nueva Categoría</a>
                        </div>
                    </form>
                )}
                {categories.length > 0 ? (
                    categories.map((category) => (
                        <div className="list" key={category.id}>
                            <div className="list-header">
                                <h3 className="pb-2">{category.name}</h3>
                                {editingCategories.has(category.id) ? <FontAwesomeIcon className="edit-button" onClick={() => handleEditCategory(category.id)} icon={faX} />
                                    : <FontAwesomeIcon id={category.id} className="edit-button" onClick={() => handleEditCategory(category.id)} icon={faPenToSquare} />}
                            </div>
                            <DragDropContext
                                onDragEnd={(result) => handleDragEnd(result, category.id)}
                            >
                                <Droppable droppableId={category.id}>
                                    {(provided) => (
                                        <ul ref={provided.innerRef} {...provided.droppableProps}>
                                            {category.items.map((item, index) => (
                                                <Draggable
                                                    key={item.id}
                                                    draggableId={item.id}
                                                    index={index}
                                                >
                                                    {(provided, snapshot) => (
                                                        <li
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className={`draggable-item ${snapshot.isDragging ? "dragging" : ""
                                                                }`}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                            }}
                                                        >
                                                            {index + 1}. {item.name}
                                                            {editingCategories.has(category.id) && (
                                                                <div className="buttons">
                                                                    <button className="delete-button"><FontAwesomeIcon onClick={() => handleDeleteItem(category.id, item.id)} icon={faTrashAlt} /></button>
                                                                </div>
                                                            )}
                                                        </li>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </ul>
                                    )}
                                </Droppable>
                            </DragDropContext>
                            {editingCategories.has(category.id) ?
                                <div className="text-center">
                                    <a style={{ color: "red", fontWeight: "bold" }} onClick={() => handleDeleteCategory(category.id)}>
                                        Eliminar Categoría
                                    </a>
                                </div> : ""}
                        </div>
                    ))
                ) : ""}
                {/*
                // Texto para cuando no hay categorias disponibles
                ) : (
                    <div className="centered-container">
                    <h3>No hay categorías disponibles.</h3>
                    </div>
                )} */}
                {isModalOpen && (
                    <Modal onClose={() => setIsModalOpen(false)}>
                        <h2>Añadir Nueva Categoría</h2>
                        <form onSubmit={(e) => { e.preventDefault(); handleSaveCategory(); }}>
                            <TextField
                                label="Nombre de la categoría"
                                variant="outlined"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                fullWidth
                                margin="normal"
                                style={{ marginTop: "5px" }}
                            />
                            <button className="main-button" type="submit">
                                Añadir
                            </button>
                        </form>
                    </Modal>
                )}
            </div>
            {isConfirmModalOpen && (
                <Modal onClose={cancelDeleteCategory}>
                    <div className="modal-body">
                        <h2>Confirmar Eliminación</h2>
                        <p>¿Estás seguro de que deseas eliminar esta categoría? </p>
                        <p>Esta acción no se puede deshacer.</p>
                        <div className="row">
                            <div className="col-6" style={{ paddingLeft: 0 }}>
                                <button className="btn btn-secondary btn-block" onClick={cancelDeleteCategory}>Cancelar</button>
                            </div>
                            <div className="col-6" style={{ paddingRight: 0 }}>
                                <button className="btn btn-danger btn-block" onClick={confirmDeleteCategory}>Eliminar</button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Ranking;
